import {
  formatIsoLocalDateTime,
  getUtcEndOfGoalMonth,
  getUtcEndOfWeekDate,
  parseGoalUpdatesFeedStartDate,
  parseProjectUpdatesFeedStartDate,
  tzUserTimezoneDate,
} from '@townsquare/date-utils';
import {
  getPendingUpdateGoalsTqlQuery,
  getPendingUpdateProjectsTqlQuery,
  getWritingModeSettingsFromLocalStorage,
} from '@townsquare/pending-updates-drawer';
import { createRelayResource } from '@townsquare/relay-utils';
import { ResourceContext } from '@townsquare/router';
import { useIsExperimentEnabledWithoutExposure } from '@townsquare/stat-sig';

import HomeHeaderViewQuery, {
  HomeHeaderViewQuery as HomeHeaderViewQueryType,
} from '../components/__generated__/HomeHeaderViewQuery.graphql';

const FRIDAY_DAY_NUM = 5;
const SATURDAY_DAY_NUM = 6;
const SUNDAY_DAY_NUM = 7;

export const homeHeaderViewResource = createRelayResource<HomeHeaderViewQueryType>({
  getQuery: (_, resourceContext) => {
    const context = resourceContext as ResourceContext;
    const { user } = context;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const isHomeHeaderEnabled = useIsExperimentEnabledWithoutExposure('townsquare_home_header_experiment');
    const currentDay = tzUserTimezoneDate().getDay();

    // Permissions
    const userHasProjectsAccess = context.userCapabilities?.includes('projects_app:access') ?? false;
    const userHasGoalsAccess = context.userCapabilities?.includes('goals_app:access') ?? false;

    // Writing Mode
    const areUpdatesDue =
      currentDay === FRIDAY_DAY_NUM || currentDay === SATURDAY_DAY_NUM || currentDay === SUNDAY_DAY_NUM;
    const writingModeSettings = getWritingModeSettingsFromLocalStorage(user.accountId);
    const pendingUpdateProjectsTqlQuery = getPendingUpdateProjectsTqlQuery(
      user.accountId,
      writingModeSettings.showPendingAndPaused !== 'off',
      writingModeSettings.showContributing !== 'off',
    );
    const pendingUpdateGoalsTqlQuery = getPendingUpdateGoalsTqlQuery(
      user.accountId,
      writingModeSettings.showPendingAndPaused !== 'off',
    );
    const loadWritingModeGoalsQuery = writingModeSettings.showGoals !== 'off' && userHasGoalsAccess && areUpdatesDue;
    const loadWritingModeProjectsQuery = userHasProjectsAccess && areUpdatesDue;

    // Reading Mode
    const projectsFeedStartDate = parseProjectUpdatesFeedStartDate();
    const goalsFeedStartDate = parseGoalUpdatesFeedStartDate();
    const formattedProjectFeedStartDate = formatIsoLocalDateTime(projectsFeedStartDate);
    const formattedGoalsFeedStartDate = formatIsoLocalDateTime(goalsFeedStartDate);
    const formattedProjectsFeedEndDate = formatIsoLocalDateTime(getUtcEndOfWeekDate(projectsFeedStartDate));
    const formattedGoalsFeedEndDate = formatIsoLocalDateTime(getUtcEndOfGoalMonth(goalsFeedStartDate));

    return {
      query: HomeHeaderViewQuery,
      variables: {
        isHomeHeaderEnabled: isHomeHeaderEnabled,
        workspaceUUID: context.workspace?.UUID ?? undefined,
        pendingUpdateGoalsTqlQuery: pendingUpdateGoalsTqlQuery,
        pendingUpdateProjectsTqlQuery: pendingUpdateProjectsTqlQuery,
        includeReadingModeGoalUpdates: userHasGoalsAccess,
        includeReadingModeProjectUpdates: userHasProjectsAccess,
        goalUpdatesCreatedBefore: formattedGoalsFeedEndDate,
        goalUpdatesCreatedAfter: formattedGoalsFeedStartDate,
        projectUpdatesCreatedBefore: formattedProjectsFeedEndDate,
        projectsUpdatesCreatedAfter: formattedProjectFeedStartDate,
        loadWritingModeProjectsQuery: loadWritingModeProjectsQuery,
        loadWritingModeGoalsQuery: loadWritingModeGoalsQuery,
      },
      cacheKey: `homeHeaderViewResource`,
    };
  },
});
